import React, { useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { CheckCircle, XCircle as XCircleIcon, Eye, EyeOff, Mail, AlertTriangle } from 'lucide-react';
import { Modal } from '../../components/ui';

// --- Google Icon SVG ---
const GoogleIcon = () => (
    <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C42.021,35.596,44,30.138,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
    </svg>
);

// --- Standalone Authentication Form ---
export function AuthForm({ auth, onGoogleSignIn }) {
    const [authMode, setAuthMode] = useState('signIn'); // 'signIn', 'signUp', 'forgotPassword'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const passwordRules = [{ label: "At least 8 characters", regex: /.{8,}/ }, { label: "At least one uppercase letter", regex: /[A-Z]/ }, { label: "At least one lowercase letter", regex: /[a-z]/ }, { label: "At least one number", regex: /[0-9]/ }, { label: "At least one special character", regex: /[^A-Za-z0-9]/ }];
    const validatePassword = () => { for (const rule of passwordRules) { if (!rule.regex.test(password)) return `Password must contain: ${rule.label}`; } return null; };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        if (authMode === 'signUp') {
            const validationError = validatePassword();
            if (validationError) {
                setError(validationError);
                return;
            }
        }
        setIsProcessing(true);
        try {
            if (authMode === 'signUp') {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            let friendlyMessage = 'An unknown error occurred. Please try again.';
            console.error("Firebase Auth Error:", err.code, err.message);
            switch (err.code) {
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                    friendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
                    break;
                case 'auth/invalid-email':
                    friendlyMessage = 'Please enter a valid email address.';
                    break;
                case 'auth/user-disabled':
                    friendlyMessage = 'This account has been disabled.';
                    break;
                case 'auth/too-many-requests':
                    friendlyMessage = 'Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.';
                    break;
                case 'auth/email-already-in-use':
                    friendlyMessage = 'An account with this email address already exists.';
                    break;
                default:
                    friendlyMessage = 'An unexpected error occurred. Please try again.';
            }
            setError(friendlyMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);

            const credential = GoogleAuthProvider.credentialFromResult(result);
            if (credential) {
                const googleIdToken = credential.idToken;
                if (onGoogleSignIn) {
                    onGoogleSignIn(googleIdToken);
                }
            } else {
                throw new Error("Could not retrieve Google credential from sign-in result.");
            }
        } catch (err) {
            console.error("Google Sign-In Error:", err);
            setError(err.message);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setIsProcessing(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage('Password reset email sent! Please check your inbox.');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const renderContent = () => {
        if (authMode === 'forgotPassword') {
            return (
                <form onSubmit={handlePasswordReset}>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2 text-center">Reset Password</h2>
                    <p className="text-center text-gray-500 dark:text-gray-400 mb-6 text-sm">Enter your email to receive a reset link.</p>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg mb-4 focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required />
                    {message && <p className="text-brand-700 dark:text-brand-400 text-sm my-4 text-center">{message}</p>}
                    {error && <p className="text-red-500 dark:text-red-400 text-sm my-4 text-center">{error}</p>}
                    <button type="submit" disabled={isProcessing} className="w-full bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors disabled:bg-slate-400 dark:disabled:bg-slate-600 flex items-center justify-center">
                        <Mail className="mr-2 h-5 w-5" />
                        {isProcessing ? 'Sending...' : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => setAuthMode('signIn')} className="w-full mt-4 text-sm text-brand-800 dark:text-brand-400 hover:underline">
                        Back to Sign In
                    </button>
                </form>
            );
        }

        return (
            <>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 text-center">{authMode === 'signUp' ? 'Create an Account' : 'Sign In'}</h2>
                <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center py-3 px-4 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 font-medium transition-colors">
                    <GoogleIcon />
                    Sign in with Google
                </button>
                <div className="my-6 flex items-center">
                    <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
                    <span className="flex-shrink mx-4 text-gray-400 dark:text-gray-500 text-sm">OR</span>
                    <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
                </div>
                <form onSubmit={handleSubmit}>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg mb-4 focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required />
                    <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg mb-2 focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-slate-600">
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {authMode === 'signIn' && (
                        <div className="text-right mb-4">
                            <button type="button" onClick={() => setAuthMode('forgotPassword')} className="text-sm text-brand-800 dark:text-brand-400 hover:underline font-medium">
                                Forgot password?
                            </button>
                        </div>
                    )}
                    {authMode === 'signUp' && (<div className="my-4 space-y-1"> {passwordRules.map(rule => (<div key={rule.label} className={`flex items-center text-sm ${rule.regex.test(password) ? 'text-brand-700 dark:text-brand-400' : 'text-gray-500 dark:text-gray-400'}`}> {rule.regex.test(password) ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircleIcon className="h-4 w-4 mr-2" />} {rule.label} </div>))} </div>)}
                    {error && <p className="text-red-500 dark:text-red-400 text-sm my-4">{error}</p>}
                    <button type="submit" disabled={isProcessing} className="w-full bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors disabled:bg-slate-400 dark:disabled:bg-slate-600">
                        {isProcessing ? 'Processing...' : (authMode === 'signUp' ? 'Create Account' : 'Sign In')}
                    </button>
                    <button type="button" onClick={() => setAuthMode(authMode === 'signIn' ? 'signUp' : 'signIn')} className="w-full mt-4 text-sm text-brand-800 dark:text-brand-400 hover:underline">
                        {authMode === 'signIn' ? "Don't have an account? Sign Up" : 'Already have an account? Sign In'}
                    </button>
                </form>
            </>
        );
    };

    return renderContent();
};


// --- Authentication Modal (Now a wrapper around the shared Modal primitive) ---
export const AuthModal = ({ auth, onClose }) => (
    <Modal isOpen={true} onClose={onClose} dismissible={false} showClose={false} size="md">
        <AuthForm auth={auth} />
    </Modal>
);

// --- Change PIN Modal ---
export const ChangePinModal = ({ onClose, onChangePin }) => {
    const [currentPin, setCurrentPin] = useState('');
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const validatePin = (pin) => /^\d{6}$/.test(pin);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validatePin(newPin)) { setError("New PIN must be exactly 6 digits."); return; }
        if (newPin !== confirmPin) { setError("New PINs do not match."); return; }
        setIsProcessing(true);
        try { await onChangePin(currentPin, newPin); onClose(); } catch (err) { setError(err.message); } finally { setIsProcessing(false); }
    };
    return (<Modal isOpen={true} onClose={onClose} dismissible={true} showClose={false} size="md"> <form onSubmit={handleSubmit}> <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Change Your PIN</h2> <div className="space-y-4"> <input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="Current PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required /> <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="New 6-Digit PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" /> <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Confirm New PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" /> </div> {error && <p className="text-red-500 dark:text-red-400 text-sm mt-4">{error}</p>} <div className="mt-6 flex justify-end space-x-4"> <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button> <button type="submit" disabled={isProcessing} className="px-6 py-2 bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold rounded-lg shadow-md disabled:bg-slate-400 dark:disabled:bg-slate-600"> {isProcessing ? 'Saving...' : 'Change PIN'} </button> </div> </form> </Modal>);
};

// --- Crypto Setup/Unlock Modal ---
export const CryptoSetupModal = ({ settings, db, userId, onUnlock, cryptoHelpers, onForgotPassword }) => {
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const isFirstTime = !settings;

    const handleUnlockSubmit = async (pinToUnlock) => {
        setError('');
        setIsProcessing(true);
        try {
            // Step 1: Derive key from the entered PIN
            const pinDerivedKey = await cryptoHelpers.deriveKey(pinToUnlock, settings.salt);

            // Step 2: Verify the PIN is correct
            const decryptedCheck = await cryptoHelpers.decrypt(settings.keyCheck, pinDerivedKey);
            if (decryptedCheck !== 'key_check_ok') {
                throw new Error("Incorrect PIN. Please try again.");
            }

            // Step 3: Handle both new and legacy account structures
            if (settings.encryptedDataKey) {
                // New system: Decrypt the persistent dataKey
                const dataKeyJwkString = await cryptoHelpers.decrypt(settings.encryptedDataKey, pinDerivedKey);
                const dataKeyJwk = JSON.parse(dataKeyJwkString);
                const finalDataKey = await window.crypto.subtle.importKey(
                    'jwk', dataKeyJwk, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']
                );
                onUnlock(finalDataKey);
            } else {
                // Legacy system: The PIN-derived key is the final key
                onUnlock(pinDerivedKey);
            }
        } catch (err) {
            console.error("Unlock failed:", err);
            setError(err.message || "Incorrect PIN. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSetup = async (e) => {
        e.preventDefault();
        setError('');
        if (isFirstTime && pin !== confirmPin) { setError("PINs do not match."); return; }
        setIsProcessing(true);
        try {
            const salt = window.crypto.getRandomValues(new Uint8Array(16));
            const pinDerivedKey = await cryptoHelpers.deriveKey(pin, salt);
            const dataKey = await cryptoHelpers.generateDataKey();
            const encryptedDataKey = await cryptoHelpers.encrypt(
                JSON.stringify(await window.crypto.subtle.exportKey('jwk', dataKey)),
                pinDerivedKey
            );
            const keyCheck = await cryptoHelpers.encrypt('key_check_ok', pinDerivedKey);
            const saltB64 = btoa(String.fromCharCode.apply(null, salt));
            await setDoc(doc(db, `users/${userId}/settings/crypto`), {
                salt: saltB64,
                keyCheck: keyCheck,
                encryptedDataKey: encryptedDataKey,
            });
            onUnlock(dataKey);
        } catch (err) {
            console.error("Setup failed:", err);
            setError("Could not create security settings. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };
    return (<Modal isOpen={true} dismissible={false} showClose={false} size="md"> <form onSubmit={isFirstTime ? handleSetup : (e) => { e.preventDefault(); handleUnlockSubmit(pin); }}> <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">{isFirstTime ? 'Create Your Secure PIN' : 'Enter Your PIN'}</h2> <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">{isFirstTime ? 'This 6-digit PIN encrypts your data. It is never stored, so do not forget it!' : 'Enter your PIN to decrypt and access your data.'}</p> <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter 6-Digit PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg mb-4 focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" /> {isFirstTime && (<input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Confirm PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg mb-4 focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" />)} {error && <p className="text-red-500 dark:text-red-400 text-sm mb-4">{error}</p>} <button type="submit" disabled={isProcessing} className="w-full bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors disabled:bg-slate-400 dark:disabled:bg-slate-600"> {isProcessing ? 'Processing...' : (isFirstTime ? 'Create & Unlock' : 'Unlock')} </button> {!isFirstTime && (<div className="text-center mt-4"><button type="button" onClick={onForgotPassword} className="text-sm text-brand-800 dark:text-brand-400 hover:underline font-medium">Forgot your PIN?</button></div>)} </form> </Modal>);
};


// --- PIN Recovery Modal ---
export const PinRecoveryModal = ({ onClose, onRecover, onSetNewPin }) => {
    const [step, setStep] = useState('initial'); // 'initial', 'recovering', 'setNewPin'
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleRecovery = async () => {
        setIsProcessing(true);
        setError('');
        try {
            await onRecover();
            setStep('setNewPin');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSetNewPinSubmit = async (e) => {
        e.preventDefault();
        if (newPin !== confirmPin) {
            setError("New PINs do not match.");
            return;
        }
        if (!/^\d{6}$/.test(newPin)) {
            setError("PIN must be exactly 6 digits.");
            return;
        }
        setIsProcessing(true);
        setError('');
        try {
            await onSetNewPin(newPin);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    return (
        <Modal isOpen={true} onClose={onClose} dismissible={true} showClose={false} size="md" className="text-center">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">PIN Recovery</h2>
            {step === 'initial' && (
                <div>
                    <div className="flex items-start bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 p-4 rounded-lg mb-6">
                        <AlertTriangle className="h-8 w-8 mr-3 flex-shrink-0" />
                        <div className="text-left text-sm">
                            <p className="font-semibold">Security Check Required</p>
                            <p className="mt-1">To recover your PIN, you must first prove your identity by signing in again. This ensures that only you can access your encrypted data.</p>
                        </div>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 mb-6">Are you sure you want to proceed?</p>
                    <div className="flex justify-center space-x-4">
                        <button onClick={onClose} className="px-6 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 font-bold rounded-lg">Cancel</button>
                        <button onClick={handleRecovery} disabled={isProcessing} className="px-6 py-2 bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold rounded-lg shadow-md disabled:bg-slate-400 dark:disabled:bg-slate-600">
                            {isProcessing ? 'Verifying...' : 'Continue'}
                        </button>
                    </div>
                    {error && <p className="text-red-500 dark:text-red-400 text-sm mt-4">{error}</p>}
                </div>
            )}

            {step === 'setNewPin' && (
                <form onSubmit={handleSetNewPinSubmit}>
                    <p className="text-slate-600 dark:text-slate-300 mb-6">Recovery successful! Please set a new 6-digit PIN for your account.</p>
                    <div className="space-y-4">
                        <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="New 6-Digit PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" />
                        <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Confirm New PIN" className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white" required maxLength="6" />
                    </div>
                    {error && <p className="text-red-500 dark:text-red-400 text-sm mt-4">{error}</p>}
                    <div className="mt-6 flex justify-end space-x-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isProcessing} className="px-6 py-2 bg-brand-700 dark:bg-brand-600 hover:bg-brand-800 dark:hover:bg-brand-500 text-white font-bold rounded-lg shadow-md disabled:bg-slate-400 dark:disabled:bg-slate-600">
                            {isProcessing ? 'Saving...' : 'Set New PIN'}
                        </button>
                    </div>
                </form>
            )}
        </Modal>
    );
};
